/**
 * How good the AI is, as data.
 *
 * Four numbers decide a tier, and they are the four a player can feel: how
 * long it takes to see something (`reaction`), how often it guards what it
 * saw (`block`), how often it takes a free opening (`punish`), and how hard it
 * comes forward (`pressure`). Everything else a tier may set is a supporting
 * number with a sensible default in `AI`.
 *
 * **None of them is a cheat.** There is no tier that reads the player's input,
 * no tier with extra reach, no tier that does more damage, and no tier that
 * knows a move is coming before the move has started. A harder opponent here is
 * one that *sees sooner and answers more often* — which is the same axis a
 * human improves along, and the only one where losing to it teaches anything.
 *
 * The delay is the load-bearing one. It is a real delay: the AI reads the other
 * fighter out of a queue of snapshots `reaction` seconds old, so a rookie is not
 * a champion with a dice roll in front of it — it is genuinely looking at an
 * older fight. A cross is 19 frames from commitment to gone, which is 0.32s: a
 * champion at 0.11 has time to put a guard up, a contender at 0.20 sometimes
 * does, and a rookie at 0.36 is answering a punch that has already landed.
 */

import DIFFICULTY from './difficulty.json';

export class DifficultyConfigError extends Error {}

/** The four a tier has to answer, and the range each one lives in. */
const REQUIRED = Object.freeze({
  /** Seconds between something happening and this fighter seeing it. */
  reaction: [0, 2],
  /** Chance of guarding a strike it has seen committed. */
  block: [0, 1],
  /** Chance of taking a whiff punish it has seen open. */
  punish: [0, 1],
  /** Chance of attacking rather than holding, on any given beat. */
  pressure: [0, 1],
});

/** What else a tier is allowed to set. Anything not here is a typo. */
const OPTIONAL = Object.freeze([
  'describe',
  'beat',
  'chain',
  'cover',
  'standoff',
  'mash',
  'margin',
  'fetch',
  'space',
  'runAt',
  'lookahead',
  'pressScale',
  'pressBeat',
  'coverScale',
]);

export function validateDifficulty(config = DIFFICULTY) {
  const tiers = config?.tiers;
  if (!tiers || typeof tiers !== 'object') {
    throw new DifficultyConfigError('difficulty needs a tiers object');
  }
  const names = Object.keys(tiers);
  // Three is the point of the exercise: a ladder needs a bottom, a middle and
  // a top, and two tiers is a switch.
  if (names.length < 3) {
    throw new DifficultyConfigError(`difficulty has ${names.length} tiers; want at least 3`);
  }
  if (!names.includes(config.default)) {
    throw new DifficultyConfigError(
      `difficulty default "${config.default}" is not one of ${names.join(', ')}`
    );
  }

  for (const name of names) {
    const tier = tiers[name];
    if (!tier || typeof tier !== 'object') {
      throw new DifficultyConfigError(`tier "${name}" must be an object`);
    }
    for (const [key, [low, high]] of Object.entries(REQUIRED)) {
      const value = tier[key];
      if (!(typeof value === 'number' && value >= low && value <= high)) {
        throw new DifficultyConfigError(
          `tier "${name}" has a ${key} of ${JSON.stringify(value)}; want ${low}..${high}`
        );
      }
    }
    if (tier.beat !== undefined) {
      const [shortest, longest] = tier.beat ?? [];
      if (!(shortest > 0) || !(longest >= shortest)) {
        throw new DifficultyConfigError(
          `tier "${name}" has a beat of ${JSON.stringify(tier.beat)}; want [shortest, longest]`
        );
      }
    }
    for (const key of Object.keys(tier)) {
      if (key in REQUIRED || OPTIONAL.includes(key)) continue;
      throw new DifficultyConfigError(`tier "${name}" sets "${key}", which is not a tuning number`);
    }
  }

  return config;
}

// A typo in the ladder fails at boot rather than at the first fight it ruins.
validateDifficulty(DIFFICULTY);

export { DIFFICULTY };

/** Every tier there is, easiest first is not implied — this is declaration order. */
export const tierNames = (config = DIFFICULTY) => Object.keys(config.tiers);

/** The tier named, or the default. Throws on a name nobody has heard of. */
export function difficultyFor(name = null, config = DIFFICULTY) {
  if (name && typeof name === 'object') return name;
  const which = name ?? config.default;
  const tier = config.tiers[which];
  if (!tier) {
    throw new DifficultyConfigError(
      `no such difficulty "${which}"; there is ${tierNames(config).join(', ')}`
    );
  }
  return { ...tier, tier: which };
}
